import { applicantSection, feeInfo, feeProofAttachment, otherAttachments, replySection } from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E7. Wraki pojazdów: zgłoszenie porzuconego pojazdu albo wniosek o zezwolenie na odbiór pojazdu z parkingu
 * strzeżonego — art. 50a i 130a Prawa o ruchu drogowym, rozporządzenie MSWiA z 22.06.2011 r. (zał. 3 — wzór zezwolenia
 * na odbiór, wystawia organ). Usunięcie pojazdu następuje z urzędu — to nie jest wniosek interesanta. Brak wzoru
 * ogólnopolskiego dla interesanta; treść jak procedura Straży Miejskiej m.st. Warszawy „Usunięcie wraku”
 * (https://www.strazmiejska.waw.pl/mieszkancy/procedura/usuniecie-wraku — pobrana 2026-10-22): zgłoszenie — marka,
 * numer rejestracyjny lub VIN, miejsce porzucenia (ulica, nr posesji, miejscowość); odbiór — dokument tożsamości
 * (zastępuje go logowanie mObywatelem), dokument upoważniający do używania pojazdu, opłata za usunięcie i parkowanie.
 * Zamiast pełnomocnictwa KPA — upoważnienie właściciela, leasingodawcy albo firmy (katalog nie przewiduje opłaty
 * skarbowej). Stawki opłat ustala rada powiatu (art. 130a ust. 6) — bez kalkulatora.
 */
const REPORT: EbiCondition = { field: "rodzajSprawy", equals: "zgloszenie" };
const PICKUP: EbiCondition = { field: "rodzajSprawy", equals: "odbior" };

const E7: IEbiFormDefinition = {
  code: "E7",
  version: 1,
  title: "Zgłoszenie porzuconego pojazdu lub wniosek o zezwolenie na odbiór pojazdu z parkingu",
  category: EEbiFormCategory.Safety,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Zgłoś porzucony pojazd (wrak) straży gminnej albo poproś o zezwolenie na odbiór swojego pojazdu usuniętego z drogi.",
  keywords: ["wrak", "porzucony pojazd", "usunięcie pojazdu", "parking strzeżony", "odbiór pojazdu", "straż miejska", "straż gminna"],
  organ: "Straż gminna (miejska); zezwolenie na odbiór pojazdu wydaje podmiot, który wydał dyspozycję jego usunięcia",
  legalBasis:
    "art. 50a i art. 130a ustawy z dnia 20 czerwca 1997 r. — Prawo o ruchu drogowym; rozporządzenie Ministra Spraw Wewnętrznych i Administracji z dnia 22 czerwca 2011 r. (Dz.U. z 2018 r. poz. 2285)",
  feeLabel: "wg stawek",
  fee: "Zgłoszenie — bez opłaty. Odbiór pojazdu — opłata za usunięcie i każdą dobę przechowywania według uchwały rady powiatu, płatna przed odbiorem; bez opłaty skarbowej.",
  variantField: "rodzajSprawy",
  variants: {
    zgloszenie: { title: "Zgłoszenie porzuconego pojazdu (wraku)", documentType: "Pismo" },
    odbior: { title: "Wniosek o wydanie zezwolenia na odbiór pojazdu z parkingu strzeżonego", documentType: "Wniosek" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj sprawy",
      fields: [
        {
          code: "rodzajSprawy",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            {
              value: "zgloszenie",
              label: "zgłoszenie porzuconego pojazdu — pozostawionego bez tablic rejestracyjnych albo takiego, którego stan wskazuje, że nie jest używany",
            },
            { value: "odbior", label: "wniosek o wydanie zezwolenia na odbiór mojego pojazdu usuniętego z drogi i umieszczonego na parkingu strzeżonym" },
          ],
        },
        {
          code: "infoZgloszenie",
          type: "info",
          label:
            "Straż gminna sprawdzi zgłoszenie; usunięcie pojazdu z drogi (art. 50a ustawy — Prawo o ruchu drogowym) następuje z urzędu, na koszt właściciela.",
          visibleWhen: REPORT,
        },
      ],
    },
    applicantSection("Zgłaszający / wnioskodawca"),
    {
      code: "zgloszenie",
      title: "Porzucony pojazd",
      visibleWhen: REPORT,
      fields: [
        { code: "zglMarka", type: "text", label: "Marka pojazdu", required: true, width: "half", maxLength: 100 },
        {
          code: "zglNrRejestracyjny",
          type: "text",
          label: "Numer rejestracyjny albo VIN (jeśli widoczny)",
          width: "half",
          maxLength: 20,
        },
        {
          code: "zglMiejsce",
          type: "text",
          label: "Dokładne miejsce porzucenia pojazdu (miejscowość, ulica, numer posesji)",
          hint: "Usunięcie z urzędu dotyczy pojazdów pozostawionych na drodze — opisz miejsce tak, by strażnicy mogli je odnaleźć.",
          required: true,
          maxLength: 300,
        },
        {
          code: "zglOpisStanu",
          type: "textarea",
          label: "Opis stanu pojazdu",
          placeholder: "np. brak tablic rejestracyjnych, przebite opony, wybite szyby, stoi od kilku miesięcy",
          required: true,
          maxLength: 2000,
        },
      ],
    },
    {
      code: "odbiorPojazdu",
      title: "Pojazd do odbioru",
      visibleWhen: PICKUP,
      fields: [
        { code: "pojazdNrRejestracyjny", type: "text", label: "Numer rejestracyjny", required: true, width: "half", maxLength: 15 },
        { code: "pojazdMarka", type: "text", label: "Marka pojazdu", required: true, width: "half", maxLength: 100 },
        { code: "pojazdVin", type: "text", label: "Numer VIN", width: "half", maxLength: 20 },
        { code: "dataUsuniecia", type: "date", label: "Data usunięcia pojazdu (jeśli znana)", width: "half" },
        { code: "miejsceUsuniecia", type: "text", label: "Miejsce, z którego usunięto pojazd", maxLength: 300 },
        { code: "nrDyspozycji", type: "text", label: "Numer dyspozycji usunięcia lub sprawy (jeśli znany)", width: "half", maxLength: 50 },
        {
          code: "tytulDoPojazdu",
          type: "radio",
          label: "Odbieram pojazd jako",
          required: true,
          defaultValue: "wlasciciel",
          options: [
            { value: "wlasciciel", label: "właściciel (współwłaściciel) albo posiadacz wpisany w dowodzie rejestracyjnym" },
            { value: "upowazniony", label: "osoba upoważniona przez właściciela pojazdu, leasingodawcę albo firmę lub instytucję, do której należy pojazd" },
          ],
        },
        feeInfo(
          "infoOdbior",
          "Pojazd wydaje się po wniesieniu opłaty za usunięcie i przechowywanie na parkingu (stawki ustala rada powiatu — art. 130a ust. 6 ustawy — Prawo o ruchu drogowym). Pojazd nieodebrany w ciągu 6 miesięcy od usunięcia przechodzi z mocy prawa na własność gminy (art. 50a ust. 2).",
        ),
      ],
    },
    replySection("Sposób odbioru odpowiedzi"),
  ],
  attachments: [
    {
      code: "zdjecia",
      label: "Zdjęcia porzuconego pojazdu",
      hint: "Nieobowiązkowo — ułatwiają strażnikom odnalezienie pojazdu i ocenę jego stanu.",
      visibleWhen: REPORT,
      multiple: true,
    },
    {
      code: "dowodRejestracyjny",
      label: "Dowód rejestracyjny pojazdu albo pokwitowanie zatrzymania dowodu rejestracyjnego",
      visibleWhen: PICKUP,
      required: true,
    },
    {
      code: "upowaznienie",
      label: "Upoważnienie właściciela pojazdu, leasingodawcy albo firmy lub instytucji, do której należy pojazd",
      visibleWhen: { all: [PICKUP, { field: "tytulDoPojazdu", equals: "upowazniony" }] },
      required: true,
    },
    feeProofAttachment("Dowód uiszczenia opłaty za usunięcie i przechowywanie pojazdu", {
      hint: "Potwierdzenie przelewu albo wpłaty — kwotę podaje podmiot prowadzący parking lub urząd (uchwała rady powiatu).",
      visibleWhen: PICKUP,
    }),
    otherAttachments(),
  ],
};

export default E7;
